import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  TableSortLabel,
  Divider,
  Grow,
  Zoom,
  Tabs,
  Tab,
  Badge
} from '@mui/material';

import {
  Search,
  Visibility,
  CheckCircle,
  Pending,
  Download,
  Refresh,
  Clear,
  Assignment,
  CalendarToday,
  Verified,
  Person,
  ThumbUp,
  LocalShipping,
  Receipt,
  AssignmentTurnedIn,
  Warning,
  HourglassEmpty,
  Inventory,
  Storefront,
  QrCode,
  Print,
  CheckCircleOutline
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import Sidebar from '../../components/sidebars/Sidebar _inventory';
import TopNav from '../../components/top_bars/TopNav_inventory';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const StyledCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
  borderRadius: '20px',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor: alpha(theme.palette.primary.main, 0.3),
  }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '16px',
  background: theme.palette.background.paper,
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
  overflowX: 'auto',
  '& .MuiTable-root': {
    minWidth: 1200,
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    fontWeight: 700,
    fontSize: '0.85rem',
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    position: 'sticky',
    top: 0,
    zIndex: 10,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  }
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const statusString = String(status || 'Pending').toLowerCase();
  
  let config = {};
  
  if (statusString === 'approved by gm') {
    config = {
      backgroundColor: alpha(theme.palette.success.main, 0.12),
      color: theme.palette.success.main,
      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
    };
  } else if (statusString === 'approved by dept head') {
    config = {
      backgroundColor: alpha(theme.palette.info.main, 0.12),
      color: theme.palette.info.main,
      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
    };
  } else if (statusString === 'issued') {
    config = {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
      color: theme.palette.primary.main,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    };
  } else if (statusString === 'received') {
    config = {
      backgroundColor: alpha(theme.palette.success.main, 0.12),
      color: theme.palette.success.main,
      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
    };
  } else {
    config = {
      backgroundColor: alpha(theme.palette.warning.main, 0.12),
      color: theme.palette.warning.main,
      border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
    };
  }
  
  return {
    borderRadius: '20px',
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '28px',
    ...config
  };
});

const StoreKeeperViewRequisition = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loggedInUser, setLoggedInUser] = useState('');
  const [userRole, setUserRole] = useState('');
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [orderBy, setOrderBy] = useState('request_date');
  const [order, setOrder] = useState('desc');
  
  // Data states
  const [requisitions, setRequisitions] = useState([]);
  const [totalRequisitions, setTotalRequisitions] = useState(0);
  const [statistics, setStatistics] = useState({
    total_pending: 0,
    total_issued: 0,
    total_received: 0,
    low_stock_alerts: 0
  });
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [issueQuantity, setIssueQuantity] = useState('');
  const [issueNotes, setIssueNotes] = useState('');
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Fetch requisitions approved by HOD/GM but not yet issued
  const fetchPendingIssuances = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (departmentFilter !== 'all') params.append('department', departmentFilter);
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      const response = await axios.get(`${API_BASE_URL}/inventory/get_pending_issuances.php?${params}`);
      
      if (response.data.success) {
        setRequisitions(response.data.data);
        setTotalRequisitions(response.data.total);
        setStatistics(response.data.statistics);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching pending issuances:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to fetch pending issuances',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, departmentFilter, page, rowsPerPage]);

  useEffect(() => {
    fetchPendingIssuances();
  }, [fetchPendingIssuances]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, departmentFilter]);

  useEffect(() => {
    const userName = localStorage.getItem('userName');
    const userEmail = localStorage.getItem('email');
    const role = localStorage.getItem('role');
    
    setLoggedInUser(userName || userEmail || 'Store Keeper');
    setUserRole(role || 'staff');
  }, []);

  // Check if user is authorized as Store Keeper
  const isAuthorizedStoreKeeper = () => {
    return userRole === 'store_keeper' || userRole === 'admin' || userRole === 'storekeeper';
  };

  // Issue item to requester
  const handleIssueItem = async () => {
    if (!selectedRequisition) return;
    
    setLoading(true);
    try {
      const payload = {
        id: selectedRequisition.id,
        issued_by: loggedInUser,
        department: selectedRequisition.department,
        quantity_issued: parseFloat(issueQuantity) || selectedRequisition.quantity_requested,
        notes: issueNotes
      };

      const response = await axios.post(`${API_BASE_URL}/inventory/issue_requisition.php`, payload);
      
      if (response.data.success) {
        setSnackbar({
          open: true,
          message: `Item issued successfully for requisition #${selectedRequisition.id}`,
          severity: 'success'
        });
        handleCloseIssueDialog();
        fetchPendingIssuances();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error issuing item:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to issue item',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel
  const handleExportToExcel = () => {
    const exportData = requisitions.map(item => ({
      'ID': item.id,
      'Department': item.department,
      'Item Name': item.item_name,
      'Quantity Requested': item.quantity_requested,
      'Unit': item.unit_of_measurement || 'N/A',
      'Requested By': item.requested_by,
      'Request Date': item.request_date,
      'Approved By HOD': item.approved_by_dept_head || 'N/A',
      'Approved By GM': item.approved_by_GM || 'N/A',
      'Available Stock': item.available_stock || 0,
      'Storage Location': item.storage_location || 'N/A',
      'Status': item.status,
      'Notes': item.notes || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pending_Issuances');
    XLSX.writeFile(wb, `pending_issuances_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    setSnackbar({
      open: true,
      message: 'Export successful!',
      severity: 'success'
    });
  };

  const handleOpenViewDialog = (requisition) => {
    setSelectedRequisition(requisition);
    setViewDialogOpen(true);
  };

  const handleOpenIssueDialog = (requisition) => {
    setSelectedRequisition(requisition);
    setIssueQuantity(requisition.quantity_requested.toString());
    setIssueNotes('');
    setIssueDialogOpen(true);
  };

  const handleCloseViewDialog = () => setViewDialogOpen(false);
  const handleCloseIssueDialog = () => setIssueDialogOpen(false);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortData = (data) => {
    if (!orderBy) return data;
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      
      if (orderBy === 'quantity_requested') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      
      if (orderBy === 'request_date') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (orderBy === 'available_stock') {
        aValue = parseFloat(aValue || 0);
        bValue = parseFloat(bValue || 0);
      }
      
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setDepartmentFilter('all');
    setPage(0);
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch(status) {
      case 'Approved by GM': return <Verified fontSize="small" />;
      case 'Approved by Dept Head': return <ThumbUp fontSize="small" />;
      case 'Issued': return <LocalShipping fontSize="small" />;
      case 'Received': return <CheckCircle fontSize="small" />;
      default: return <Pending fontSize="small" />;
    }
  };

  // Check if stock is sufficient
  const isStockSufficient = (requisition) => {
    return (requisition.available_stock || 0) >= requisition.quantity_requested;
  };

  // Render statistics cards
  const renderStatisticsCards = () => (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Zoom in={true} style={{ transitionDelay: '0ms' }}>
          <StyledCard>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Pending Issuance
                  </Typography>
                  <Typography variant="h3" fontWeight={700} mt={1} color="warning.main">
                    {statistics.total_pending}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main, width: 56, height: 56 }}>
                  <HourglassEmpty sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Requisitions ready for issuance
                </Typography>
              </Box>
            </CardContent>
          </StyledCard>
        </Zoom>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Zoom in={true} style={{ transitionDelay: '100ms' }}>
          <StyledCard>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Total Issued
                  </Typography>
                  <Typography variant="h3" fontWeight={700} mt={1} color="primary.main">
                    {statistics.total_issued}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, width: 56, height: 56 }}>
                  <LocalShipping sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Items issued this month
                </Typography>
              </Box>
            </CardContent>
          </StyledCard>
        </Zoom>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Zoom in={true} style={{ transitionDelay: '200ms' }}>
          <StyledCard>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Completed
                  </Typography>
                  <Typography variant="h3" fontWeight={700} mt={1} color="success.main">
                    {statistics.total_received}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main, width: 56, height: 56 }}>
                  <AssignmentTurnedIn sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Fully completed requisitions
                </Typography>
              </Box>
            </CardContent>
          </StyledCard>
        </Zoom>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Zoom in={true} style={{ transitionDelay: '300ms' }}>
          <StyledCard>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Low Stock Alerts
                  </Typography>
                  <Typography variant="h3" fontWeight={700} mt={1} color="error.main">
                    {statistics.low_stock_alerts}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main, width: 56, height: 56 }}>
                  <Warning sx={{ fontSize: 32 }} />
                </Avatar>
              </Box>
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary">
                  Items below threshold
                </Typography>
              </Box>
            </CardContent>
          </StyledCard>
        </Zoom>
      </Grid>
    </Grid>
  );

  // Render requisitions table
  const renderRequisitionsTable = () => (
    <StyledTableContainer>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Department</TableCell>
            <TableCell>Requested By</TableCell>
            <TableCell>Item Name</TableCell>
            <TableCell align="right">Qty</TableCell>
            <TableCell>Unit</TableCell>
            <TableCell align="right">Available Stock</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Request Date</TableCell>
            <TableCell>Approvals</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : sortData(requisitions).length === 0 ? (
            <TableRow>
              <TableCell colSpan={11} align="center" sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">
                  No pending issuances found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortData(requisitions).map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>#{item.id}</Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={item.department} 
                    size="small" 
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <Person sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                    </Avatar>
                    <Typography variant="body2">{item.requested_by}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <Inventory sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={500}>
                    {Number(item.quantity_requested).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>{item.unit_of_measurement || 'N/A'}</TableCell>
                <TableCell align="right">
                  <Chip 
                    size="small"
                    label={`${Number(item.available_stock || 0).toLocaleString()} ${item.unit_of_measurement || ''}`}
                    color={(item.available_stock || 0) < item.quantity_requested ? 'error' : 'success'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{item.storage_location || 'Not specified'}</Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CalendarToday sx={{ fontSize: 12, color: 'text.secondary' }} />
                    <Typography variant="caption">{item.request_date}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {item.approved_by_dept_head && (
                      <Tooltip title={`HOD: ${item.approved_by_dept_head}`}>
                        <Verified fontSize="small" color="info" />
                      </Tooltip>
                    )}
                    {item.approved_by_GM && (
                      <Tooltip title={`GM: ${item.approved_by_GM}`}>
                        <Verified fontSize="small" color="success" />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleOpenViewDialog(item)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={!isStockSufficient(item) ? "Insufficient Stock" : "Issue Item"}>
                      <span>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleOpenIssueDialog(item)}
                          disabled={!isStockSufficient(item)}
                        >
                          <LocalShipping fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={totalRequisitions}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </StyledTableContainer>
  );

  // Check authorization
  if (!isAuthorizedStoreKeeper()) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="error" variant="filled">
          Access Denied. Store Keeper privileges required.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box sx={{
        flexGrow: 1,
        p: 3,
        ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
        transition: 'margin 0.3s ease-in-out',
        maxWidth: '1600px',
        mx: 'auto',
        width: '100%'
      }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            window.location.href = '/login';
          }}
          onNotificationClick={() => console.log('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ mt: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <LocalShipping sx={{ mr: 2, verticalAlign: 'middle', fontSize: 32, color: theme.palette.primary.main }} />
              Store Keeper - Item Issuance
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Review and issue approved requisitions to department staff
            </Typography>
          </Box>

          {/* Statistics Cards */}
          {renderStatisticsCards()}

          {/* Main Content */}
          <Paper elevation={0} sx={{ borderRadius: '20px', overflow: 'hidden' }}>
            {/* Header Bar */}
            <Box sx={{ 
              p: 3, 
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Storefront sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      Pending Issuances
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Requisitions approved and ready for issuance
                    </Typography>
                  </Box>
                </Box>
                
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleExportToExcel}
                    size="small"
                  >
                    Export
                  </Button>
                  <IconButton onClick={() => fetchPendingIssuances()} size="small">
                    <Refresh />
                  </IconButton>
                </Stack>
              </Box>
            </Box>

            {/* Search and Filters */}
            <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Search by item name, requester, department..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color="action" fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchQuery('')}>
                            <Clear fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Department</InputLabel>
                      <Select
                        value={departmentFilter}
                        onChange={(e) => setDepartmentFilter(e.target.value)}
                        label="Department"
                      >
                        <MenuItem value="all">All Departments</MenuItem>
                        <MenuItem value="ICT">ICT</MenuItem>
                        <MenuItem value="Store Keeping">Store Keeping</MenuItem>
                        <MenuItem value="Account & Auditing">Account & Auditing</MenuItem>
                        <MenuItem value="Admin">Admin</MenuItem>
                      </Select>
                    </FormControl>
                    
                    {(searchQuery || departmentFilter !== 'all') && (
                      <Button size="small" onClick={clearFilters} startIcon={<Clear />}>
                        Clear
                      </Button>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            {/* Table */}
            {renderRequisitionsTable()}
          </Paper>
        </Box>
      </Box>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" component="span" fontWeight={600}>
              Requisition Details #{selectedRequisition?.id}
            </Typography>
            {selectedRequisition && (
              <StatusChip label={selectedRequisition.status} status={selectedRequisition.status} size="small" />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRequisition && (
            <>
              <Typography variant="subtitle2" fontWeight={600} color="primary.main" gutterBottom sx={{ mb: 2 }}>
                Request Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Department</Typography>
                  <Typography variant="body1" fontWeight={500}>{selectedRequisition.department}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Requested By</Typography>
                  <Typography variant="body1">{selectedRequisition.requested_by}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Item Name</Typography>
                  <Typography variant="body1" fontWeight={500}>{selectedRequisition.item_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Quantity Requested</Typography>
                  <Typography variant="body1">
                    {Number(selectedRequisition.quantity_requested).toLocaleString()} {selectedRequisition.unit_of_measurement || ''}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Available Stock</Typography>
                  <Typography variant="body1" color={(selectedRequisition.available_stock || 0) < selectedRequisition.quantity_requested ? 'error.main' : 'success.main'}>
                    {Number(selectedRequisition.available_stock || 0).toLocaleString()} {selectedRequisition.unit_of_measurement || ''}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Storage Location</Typography>
                  <Typography variant="body1">{selectedRequisition.storage_location || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Request Date</Typography>
                  <Typography variant="body1">{selectedRequisition.request_date}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Approval Chain</Typography>
                  <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                    <Chip 
                      label={`HOD: ${selectedRequisition.approved_by_dept_head || 'Pending'}`}
                      color={selectedRequisition.approved_by_dept_head ? 'info' : 'default'}
                      size="small"
                    />
                    <Chip 
                      label={`GM: ${selectedRequisition.approved_by_GM || 'Pending'}`}
                      color={selectedRequisition.approved_by_GM ? 'success' : 'default'}
                      size="small"
                    />
                  </Stack>
                </Grid>
                {selectedRequisition.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Notes / Purpose</Typography>
                    <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
                      <Typography variant="body2">{selectedRequisition.notes}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              {(selectedRequisition.available_stock || 0) < selectedRequisition.quantity_requested && (
                <Alert severity="error" icon={<Warning />} sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight={500}>Insufficient Stock Warning!</Typography>
                  <Typography variant="caption">
                    Available stock is less than requested quantity. Please advise the department or reorder stock.
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Close</Button>
          {selectedRequisition && isStockSufficient(selectedRequisition) && (
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<LocalShipping />}
              onClick={() => {
                handleCloseViewDialog();
                handleOpenIssueDialog(selectedRequisition);
              }}
            >
              Issue Item
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Issue Confirmation Dialog */}
      <Dialog open={issueDialogOpen} onClose={handleCloseIssueDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LocalShipping color="primary" />
            <Typography variant="h6" component="span" fontWeight={600}>
              Issue Item to Requester
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Confirm issuance of the following item:
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: alpha(theme.palette.grey[500], 0.05) }}>
            <Typography variant="body2">
              <strong>Department:</strong> {selectedRequisition?.department}<br />
              <strong>Item:</strong> {selectedRequisition?.item_name}<br />
              <strong>Requested By:</strong> {selectedRequisition?.requested_by}<br />
              <strong>Requested Quantity:</strong> {selectedRequisition?.quantity_requested} {selectedRequisition?.unit_of_measurement}<br />
              <strong>Available Stock:</strong> {selectedRequisition?.available_stock} {selectedRequisition?.unit_of_measurement}
            </Typography>
          </Paper>
          
          <TextField
            fullWidth
            type="number"
            label="Quantity to Issue"
            value={issueQuantity}
            onChange={(e) => setIssueQuantity(e.target.value)}
            sx={{ mt: 2 }}
            helperText={`Max: ${selectedRequisition?.available_stock || 0} ${selectedRequisition?.unit_of_measurement || ''}`}
            InputProps={{
              inputProps: { 
                min: 0.001, 
                max: selectedRequisition?.available_stock || 0,
                step: 0.001
              }
            }}
          />
          
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Issuance Notes (Optional)"
            value={issueNotes}
            onChange={(e) => setIssueNotes(e.target.value)}
            placeholder="Add any remarks about this issuance..."
            sx={{ mt: 2 }}
          />
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              Once issued, the requester will be notified and stock will be deducted from inventory.
              The requester will need to confirm receipt.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseIssueDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleIssueItem}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <LocalShipping />}
          >
            {loading ? 'Processing...' : 'Confirm Issuance'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Grow}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StoreKeeperViewRequisition;